import { DesignSystem, DesignSystemVersion } from 'dragonmouse-shared/interfaces';
import { useNavigate } from 'react-router';
import * as routes from 'routes';
import { Loader } from 'components/Widget/Loader';
import { useState } from 'react';
import * as S from './styles';

interface ActiveDesignSystemSectionProps {
	activeDesignSystem: DesignSystem;
	linkedVersion: DesignSystemVersion | null;
	latestVersion: DesignSystemVersion | null;
	isMobile: boolean;
	onLinkDs: (designSystem: DesignSystem | null) => Promise<void>;
	onUpgrade?: () => Promise<void>;
	onClose: () => void;
	updatePendingState?: 'publishing' | 'ready' | null;
}

const ActiveDesignSystemSection = ({
	activeDesignSystem,
	linkedVersion,
	latestVersion,
	isMobile,
	onLinkDs,
	onUpgrade,
	onClose,
	updatePendingState = null,
}: ActiveDesignSystemSectionProps) => {
	const navigate = useNavigate();
	const [isUpgrading, setIsUpgrading] = useState(false);

	// Calculate version display
	const dsVersion = linkedVersion ? `v${linkedVersion.versionNumber}` : '';
	const isLatest =
		linkedVersion &&
		latestVersion &&
		linkedVersion.versionNumber === latestVersion.versionNumber &&
		latestVersion.manifest?.promptGenerationStatus === 'complete';

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onEdit = () => {
		navigate(routes.designSystem(activeDesignSystem.id, activeDesignSystem.title));
		onClose();
	};

	const onUnlink = () => {
		onLinkDs(null);
	};

	const handleUpgrade = async () => {
		if (!onUpgrade) return;
		setIsUpgrading(true);
		try {
			await onUpgrade();
			onClose();
		} catch (error) {
			// It is the responsibility of onUpgrade to handle the error
			console.error('Failed to upgrade design system version:', error);
		} finally {
			setIsUpgrading(false);
		}
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	return (
		<>
			<S.Base>
				<S.Header>
					<S.CheckIcon>✓</S.CheckIcon>
					<S.Name>{activeDesignSystem.title}</S.Name>
					{dsVersion && (
						<S.VersionBadge>{isLatest ? `Latest (${dsVersion})` : dsVersion}</S.VersionBadge>
					)}
				</S.Header>
			</S.Base>
			<S.Buttons>
				{!isMobile && (
					<S.Button primary onClick={onEdit}>
						Edit System
					</S.Button>
				)}
				<S.Button onClick={onUnlink}>Unlink</S.Button>
			</S.Buttons>
			{updatePendingState && linkedVersion && latestVersion && (
				<S.VersionSection>
					<S.SectionLabel>Version Management</S.SectionLabel>
					<S.VersionItem>
						<S.VersionDot green />
						<S.VersionInfo>
							<S.VersionLabel>{dsVersion} (Current)</S.VersionLabel>
						</S.VersionInfo>
					</S.VersionItem>
					<S.VersionItem>
						<S.VersionDot blue />
						<S.VersionInfo>
							<S.VersionLabel>
								v{latestVersion.versionNumber} (Latest)
								{updatePendingState === 'ready' && <S.ReadyBadge>READY</S.ReadyBadge>}
							</S.VersionLabel>
						</S.VersionInfo>
					</S.VersionItem>
					{updatePendingState === 'publishing' ? (
						<S.ProcessingButton disabled>
							<Loader width={12} height={12} />
							Preparing v{latestVersion.versionNumber} for use
						</S.ProcessingButton>
					) : (
						<S.UpdateButton onClick={handleUpgrade} disabled={isUpgrading || !onUpgrade}>
							{isUpgrading ? (
								<>
									<Loader width={12} height={12} />
									Updating to v{latestVersion.versionNumber}...
								</>
							) : (
								'Update Project'
							)}
						</S.UpdateButton>
					)}
				</S.VersionSection>
			)}
		</>
	);
};

export default ActiveDesignSystemSection;
